import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { dateFormat } from "@/utils";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { useUserStore } from "@/views/stores";
import type { GroupCompanyOption, SupplierPageResult, SupplierQuery, SupplierRow } from "./types";

const defaultQuery: SupplierQuery = { page: 1, size: 10 };
// 与旧 Vue ENTERPRISE_SUPPLIER_TYPE 保持同一数值和文案，筛选值直接提交分页接口。
const supplierTypeLabels: Record<number, string> = {
    1: "矿山",
    2: "砂场",
    3: "矿山加工厂",
    4: "贸易商",
    5: "其他"
};
const showLocationLabels: Record<number, string> = {
    1: "合作矿厂位置1",
    2: "合作矿厂位置2",
    3: "合作矿厂位置3",
    4: "合作矿厂位置4",
    5: "合作矿厂位置5",
    6: "合作矿厂位置6",
    7: "合作矿厂位置7",
    8: "合作矿厂位置8"
};

/** 按旧 Vue 列表的 DATETIME_PICKER_FORMAT 展示供应商创建时间。 */
function formatSupplierCreatedTime(value?: string): string {
    return dateFormat(value, DATETIME_PICKER_FORMAT);
}

type SupplierFilterValues = {
    supplierName?: string;
    type?: number[];
    groupCompanyId?: number;
};

export interface SupplierPageProps {
    deleteSupplier?: (id: number | string) => Promise<void>;
    loadGroupCompanies?: (name: string) => Promise<GroupCompanyOption[]>;
    loadSuppliers?: (query: SupplierQuery) => Promise<SupplierPageResult>;
    permissions?: string[];
}

/** 复用 Vue 供应商分页接口。 */
export async function loadSuppliers(query: SupplierQuery): Promise<SupplierPageResult> {
    const response = await request.service.get<SupplierPageResult>("/admin/supplier-admin/paging", { params: query });
    return response.data;
}

/** 复用 Vue 供应商删除接口，保留后端路径而非通用 REST 路径。 */
export async function deleteSupplier(id: number | string): Promise<void> {
    await request.service.delete(`/admin/supplier-admin/delete/${id}`);
}

/** 复用 Vue 集团远程选择接口，搜索词仍使用旧契约的 name 参数。 */
export async function loadGroupCompanies(name: string): Promise<GroupCompanyOption[]> {
    const response = await request.service.get<GroupCompanyOption[]>("/admin/group-company/selector", {
        params: { name }
    });
    return response.data;
}

/** 将 Vue 供应商分页返回的类型和展示位置枚举统一为列表文案使用的数字。 */
function normalizeSupplierRows(rows?: SupplierRow[]): SupplierRow[] {
    return (rows || []).map(row => ({
        ...row,
        supplier: row.supplier
            ? {
                  ...row.supplier,
                  type: row.supplier.type == null ? undefined : Number(row.supplier.type),
                  showLocation: row.supplier.showLocation == null ? undefined : Number(row.supplier.showLocation)
              }
            : undefined
    }));
}

/** 供应商管理列表，完整复用旧 Vue 的筛选、表格列与供应商操作契约。 */
export function SupplierPage({
    deleteSupplier: remove = deleteSupplier,
    loadGroupCompanies: loadGroups = loadGroupCompanies,
    loadSuppliers: load = loadSuppliers,
    permissions
}: SupplierPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeButtonBanned = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SUPPLIER_DEAL");
    // 沿用旧 Vue 的全局按钮禁用契约；注入 permissions 时同步支持隔离测试和页面预览。
    const buttonBanned = permissions ? permissions.includes("ROLE_BUTTON_BAN") : storeButtonBanned;
    const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [groupCompanyOptions, setGroupCompanyOptions] = useState<GroupCompanyOption[]>([]);
    const [groupCompanyLoading, setGroupCompanyLoading] = useState(false);
    const [query, setQuery] = useState<SupplierQuery>(defaultQuery);
    const [filterForm] = Form.useForm<SupplierFilterValues>();

    const refresh = async (targetQuery: SupplierQuery = query) => {
        const result = await load(targetQuery);
        setSuppliers(normalizeSupplierRows(result.list));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setSuppliers(normalizeSupplierRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("供应商加载失败");
                    setSuppliers([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    const handleDelete = async (id?: number | string) => {
        if (id == null) return;
        if (!(await confirmDialog("是否删除"))) return;
        try {
            // 删除或刷新失败时保留当前供应商列表，并展示可恢复的 Vue 兼容错误提示。
            await remove(id);
            notifyOperationSuccess("已删除");
            await refresh();
            setErrorMessage(null);
        } catch {
            setErrorMessage("供应商删除失败");
        }
    };

    // 将旧 Vue 供应商名称筛选转换为供应商分页接口参数。
    const submitFilters = ({ groupCompanyId, supplierName, type }: SupplierFilterValues) => {
        setErrorMessage(null);
        setQuery({
            ...defaultQuery,
            ...(supplierName ? { supplierName } : {}),
            ...(type?.length ? { type: type.map(Number) } : {}),
            ...(groupCompanyId ? { groupCompanyId: Number(groupCompanyId) } : {})
        });
    };

    // 清空筛选项和远程候选项，恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setGroupCompanyOptions([]);
        setQuery(defaultQuery);
    };

    // 旧 Vue remote select 在打开和清空关键词时也会请求空关键词，确保下拉候选不出现空态。
    const searchGroupCompanies = async (name: string) => {
        setGroupCompanyLoading(true);
        try {
            const options = await loadGroups(name.trim());
            setGroupCompanyOptions(options || []);
        } catch {
            setGroupCompanyOptions([]);
        } finally {
            setGroupCompanyLoading(false);
        }
    };

    // 保留旧 Vue 供应商 ProTable 的创建时间排序参数，映射为分页接口 sortKey/sortOrder。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(currentQuery => {
                const nextQuery = { ...currentQuery, page: 1 };
                if (!order) {
                    nextQuery.sortKey = "";
                    nextQuery.sortOrder = "";
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接旧 Vue 供应商 ProTable 的嵌套字段和操作插槽。
    const columns: TableColumnProps<SupplierRow>[] = [
        {
            key: "code",
            label: "序号",
            width: 78,
            prop: "supplier.code",
            render: ({ row }) => (
                <Button
                    aria-label="查看供应商详情"
                    type="link"
                    onClick={() =>
                        void navigate(`/supplierManagement/enterpriseManage/detail?id=${row.supplier?.id || ""}`)
                    }
                >
                    {row.supplier?.code || DEFAULT_VALUE}
                </Button>
            )
        },
        {
            key: "supplierName",
            label: "供应商名称",
            minWidth: 120,
            prop: "supplier.supplierName",
            render: ({ row }) => row.supplier?.supplierName || DEFAULT_VALUE
        },
        {
            key: "type",
            label: "供应商类型",
            width: 120,
            prop: "supplier.type",
            render: ({ row }) => supplierTypeLabels[row.supplier?.type || 0] || DEFAULT_VALUE
        },
        {
            key: "groupCompanyName",
            label: "所属集团",
            minWidth: 106,
            prop: "supplier.groupCompanyName",
            render: ({ row }) => row.supplier?.groupCompanyName || DEFAULT_VALUE
        },
        {
            key: "legalRepresentative",
            label: "法人姓名",
            width: 106,
            prop: "supplier.legalRepresentative",
            render: ({ row }) => row.supplier?.legalRepresentative || DEFAULT_VALUE
        },
        {
            key: "socialCreditCode",
            label: "统一社会信用代码",
            width: 204,
            prop: "supplier.socialCreditCode",
            render: ({ row }) => row.supplier?.socialCreditCode || DEFAULT_VALUE
        },
        {
            key: "location",
            label: "所在地",
            minWidth: 92,
            prop: "systemName",
            render: ({ row }) => `${row.supplier?.province || DEFAULT_VALUE}${row.supplier?.city || DEFAULT_VALUE}`
        },
        {
            key: "showLocation",
            label: "展示位置",
            width: 148,
            prop: "supplier.showLocation",
            render: ({ row }) => showLocationLabels[row.supplier?.showLocation || 0] || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 154,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => formatSupplierCreatedTime(row.supplier?.createdTime)
        },
        {
            key: "operation",
            label: "操作",
            // 旧 Vue 源码声明 240px；适配器会按截图中的双链接实际宽度压缩为 134px。
            width: 240,
            prop: "operation",
            fixed: "right",
            className: "admin-table-operation-column compact supplier-operation-column",
            render: ({ row }) => {
                if (!canWrite) return null;
                const id = row.supplier?.id;
                return (
                    <Space>
                        <Button
                            aria-label="编辑供应商"
                            disabled={buttonBanned}
                            size="small"
                            onClick={() => void navigate(`/supplierManagement/enterpriseManage/edit?id=${id || ""}`)}
                        >
                            修改
                        </Button>
                        {!row.productConsignment?.length ? (
                            <Button
                                aria-label="删除供应商"
                                danger
                                disabled={buttonBanned}
                                size="small"
                                onClick={() => void handleDelete(id)}
                            >
                                删除
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="supplier-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<SupplierRow>
                    data={suppliers}
                    columns={normalizeOperationColumns(columns, suppliers)}
                    pagination={false}
                    rowKey={record => String(record.supplier?.id || record.supplier?.code)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                供应商管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canWrite ? (
                                    <Button
                                        aria-label="新增供应商"
                                        className="admin-table-primary-action"
                                        disabled={buttonBanned}
                                        type="primary"
                                        onClick={() => void navigate("/supplierManagement/enterpriseManage/add")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Form.Item label="供应商名称" name="supplierName">
                                    <Input placeholder="请输入供应商名称搜索" />
                                </Form.Item>
                                <Form.Item label="所属集团" name="groupCompanyId">
                                    <Select
                                        allowClear
                                        aria-label="所属集团"
                                        className="supplier-filter-group"
                                        filterOption={false}
                                        showSearch
                                        loading={groupCompanyLoading}
                                        options={groupCompanyOptions.map(option => ({
                                            label: option.name,
                                            value: option.id
                                        }))}
                                        classNames={{ popup: { root: "supplier-filter-group-dropdown" } }}
                                        placeholder="请输入关键字选择集团搜索"
                                        onSearch={value => void searchGroupCompanies(value)}
                                    />
                                </Form.Item>
                                <Form.Item label="供应商类型" name="type">
                                    <Select
                                        maxTagCount={1}
                                        mode="multiple"
                                        allowClear
                                        aria-label="供应商类型"
                                        className="supplier-filter-type"
                                        options={Object.entries(supplierTypeLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择供应商类型"
                                    />
                                </Form.Item>
                                <Button htmlType="submit" icon={<TableSearchIcon name="search" />} type="primary">
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置供应商筛选"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default SupplierPage;
